import React, { useState, useEffect } from 'react';
import {
  Box,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
  CircularProgress,
  Alert,
  Snackbar
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import SettingsIcon from '@mui/icons-material/Settings';
import PeopleIcon from '@mui/icons-material/People';
import ToggleOnIcon from '@mui/icons-material/ToggleOn';
import { systemSettingsService } from '../../../services/systemSettings';
import CompetitionDefaultsForm from './CompetitionDefaultsForm';
import UserDefaultsForm from './UserDefaultsForm';
import FeatureFlagsForm from './FeatureFlagsForm';

const SystemSettingsTab = () => {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [expanded, setExpanded] = useState('competition');

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      setLoading(true);
      const data = await systemSettingsService.getSettings();
      setSettings(data);
      setError(null);
    } catch (err) {
      console.error('Error loading settings:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleAccordionChange = (panel) => (event, isExpanded) => {
    setExpanded(isExpanded ? panel : false);
  };

  const handleSaveCompetitionDefaults = async (defaults) => {
    try {
      await systemSettingsService.updateCompetitionDefaults(defaults);
      setSettings(prev => ({ ...prev, competition_defaults: defaults }));
      setSnackbar({ open: true, message: 'Competition defaults saved', severity: 'success' });
    } catch (err) {
      console.error('Error saving competition defaults:', err);
      setSnackbar({ open: true, message: err.message, severity: 'error' });
    }
  };

  const handleSaveUserDefaults = async (defaults) => {
    try {
      await systemSettingsService.updateUserDefaults(defaults);
      setSettings(prev => ({ ...prev, user_defaults: defaults }));
      setSnackbar({ open: true, message: 'User defaults saved', severity: 'success' });
    } catch (err) {
      console.error('Error saving user defaults:', err);
      setSnackbar({ open: true, message: err.message, severity: 'error' });
    }
  };

  const handleSaveFeatureFlags = async (flags) => {
    try {
      await systemSettingsService.updateFeatureFlags(flags);
      setSettings(prev => ({ ...prev, feature_flags: flags }));
      setSnackbar({ open: true, message: 'Feature flags saved', severity: 'success' });
    } catch (err) {
      console.error('Error saving feature flags:', err);
      setSnackbar({ open: true, message: err.message, severity: 'error' });
    }
  };

  const handleSnackbarClose = () => {
    setSnackbar(prev => ({ ...prev, open: false }));
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mb: 2 }}>
        Error loading settings: {error}
      </Alert>
    );
  }

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        System Settings
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Configure default values and feature flags for the application.
      </Typography>

      <Accordion
        expanded={expanded === 'competition'}
        onChange={handleAccordionChange('competition')}
      >
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <SettingsIcon sx={{ mr: 2, color: 'primary.main' }} />
          <Box>
            <Typography>Competition Defaults</Typography>
            <Typography variant="caption" color="text.secondary">
              Default settings for new competitions
            </Typography>
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          <CompetitionDefaultsForm
            defaults={settings?.competition_defaults || {}}
            onSave={handleSaveCompetitionDefaults}
          />
        </AccordionDetails>
      </Accordion>

      <Accordion
        expanded={expanded === 'user'}
        onChange={handleAccordionChange('user')}
      >
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <PeopleIcon sx={{ mr: 2, color: 'primary.main' }} />
          <Box>
            <Typography>User Defaults</Typography>
            <Typography variant="caption" color="text.secondary">
              Default settings for new users
            </Typography>
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          <UserDefaultsForm
            defaults={settings?.user_defaults || {}}
            onSave={handleSaveUserDefaults}
          />
        </AccordionDetails>
      </Accordion>

      <Accordion
        expanded={expanded === 'features'}
        onChange={handleAccordionChange('features')}
      >
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <ToggleOnIcon sx={{ mr: 2, color: 'primary.main' }} />
          <Box>
            <Typography>Feature Flags</Typography>
            <Typography variant="caption" color="text.secondary">
              Enable or disable application features
            </Typography>
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          <FeatureFlagsForm
            flags={settings?.feature_flags || {}}
            onSave={handleSaveFeatureFlags}
          />
        </AccordionDetails>
      </Accordion>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      >
        <Alert onClose={handleSnackbarClose} severity={snackbar.severity}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default SystemSettingsTab;
